import { MessageCircle, Mail, Instagram, Facebook } from "lucide-react";
import Logo from "./Logo";
import { useLanguage } from "@/contexts/LanguageContext";

const Footer = () => {
  const { t } = useLanguage();
  
  return (
    <footer className="relative py-16 md:py-20 overflow-hidden">
      {/* Icon Background Pattern */}
      <div className="absolute inset-0 opacity-5 text-6xl pointer-events-none">
        <div className="absolute top-10 left-10">🚀</div>
        <div className="absolute top-20 right-20">⛽</div>
        <div className="absolute bottom-20 left-1/4">🔥</div>
        <div className="absolute bottom-10 right-1/3">💨</div>
        <div className="absolute top-1/3 right-10">✨</div>
      </div>
      
      <div className="container mx-auto px-4 relative z-10">
        {/* 4 Column Layout with Glass Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 md:gap-6 max-w-6xl mx-auto mb-12">
          {/* Column 1: Logo + Tagline */}
          <div className="glass-morphism border border-orange-500/20 rounded-2xl p-6 space-y-4 text-center md:text-left hover:scale-105 transition-transform duration-300">
            <Logo className="h-8 w-auto mx-auto md:mx-0" variant="horizontal" theme="dark" />
            <p className="text-sm text-foreground/90 leading-relaxed">
              {t("footer.tagline")}
            </p>
            <p className="text-xs text-orange-400/90 italic font-semibold">
              {t("hero.bridge")} 🚀
            </p>
          </div>

          {/* Column 2: Product Links */}
          <div className="glass-morphism border border-neon-cyan/20 rounded-2xl p-6 space-y-4 text-center md:text-left hover:scale-105 transition-transform duration-300">
            <h4 className="font-heading font-semibold text-base bg-gradient-to-r from-white to-neon-purple bg-clip-text text-transparent">{t("footer.product")}</h4>
            <ul className="space-y-3">
              <li>
                <a href="#pricing" className="text-sm text-foreground/80 hover:text-neon-purple transition-colors">
                  {t("nav.pricing")}
                </a>
              </li>
              <li>
                <a href="#how-it-works" className="text-sm text-foreground/80 hover:text-neon-cyan transition-colors">
                  {t("nav.howItWorks")}
                </a>
              </li>
              <li>
                <a href="#faq" className="text-sm text-foreground/80 hover:text-neon-purple transition-colors">
                  {t("nav.faq")}
                </a>
              </li>
              <li>
                <a href="#calculator" className="text-sm text-foreground/80 hover:text-neon-cyan transition-colors">
                  {t("roi.title")}
                </a>
              </li>
            </ul>
          </div>

          {/* Column 3: Company Links */}
          <div className="glass-morphism border border-neon-pink/20 rounded-2xl p-6 space-y-4 text-center md:text-left hover:scale-105 transition-transform duration-300">
            <h4 className="font-heading font-semibold text-base bg-gradient-to-r from-white to-neon-pink bg-clip-text text-transparent">{t("footer.company")}</h4>
            <ul className="space-y-3">
              <li>
                <a href="/blog/what-is-fuel72" className="text-sm text-foreground/80 hover:text-neon-pink transition-colors">
                  {t("blog.relatedWhatIsAiUgc")}
                </a>
              </li>
              <li>
                <a href="/blog" className="text-sm text-foreground/80 hover:text-neon-purple transition-colors">
                  Blog
                </a>
              </li>
              <li>
                <a href="#order-form" className="text-sm text-foreground/80 hover:text-neon-pink transition-colors">
                  {t("nav.contact")}
                </a>
              </li>
              <li>
                <a href="/impressum" className="text-sm text-foreground/80 hover:text-neon-purple transition-colors">
                  Impressum
                </a>
              </li>
            </ul>
          </div>

          {/* Column 4: Contact */}
          <div className="glass-morphism border border-neon-cyan/20 rounded-2xl p-6 space-y-4 text-center md:text-left hover:scale-105 transition-transform duration-300">
            <h4 className="font-heading font-semibold text-base bg-gradient-to-r from-white to-neon-cyan bg-clip-text text-transparent">{t("footer.contact")}</h4>
            <div className="space-y-3">
              <div>
                <div className="text-xs text-foreground/60 mb-1">Email:</div>
                <a 
                  href="mailto:getfuel72@gmail.com"
                  className="text-sm text-foreground/90 hover:text-neon-purple transition-colors"
                >
                  getfuel72@gmail.com
                </a>
              </div>
              <div>
                <div className="text-xs text-foreground/60 mb-1">WhatsApp:</div>
                <a 
                  href="https://wa.me/4917687870011"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-foreground/90 hover:text-neon-cyan transition-colors"
                >
                  +49 176 87870011
                </a>
              </div>
              <div>
                <div className="text-xs text-foreground/60 mb-1">{t("footer.responseTime")}:</div>
                <div className="text-sm text-neon-green font-semibold">
                  {t("footer.underTwoHours")}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Trust Badges */}
        <div className="glass-morphism border border-orange-500/20 rounded-2xl p-6 mb-8 max-w-4xl mx-auto">
          <div className="flex flex-wrap justify-center gap-6 text-xs text-foreground/80">
            <div className="flex items-center gap-2">
              <span className="text-neon-green text-base animate-glow-pulse-green">⛽</span>
              <span>{t("footer.trustBadge1")}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-orange-400 text-base animate-flame-flicker">🔥</span>
              <span>{t("footer.trustBadge2")}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-neon-cyan text-base animate-glow-pulse">🚀</span>
              <span>{t("footer.trustBadge3")}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-neon-pink text-base animate-glow-pulse">✓</span>
              <span>No Contracts</span>
            </div>
          </div>
        </div>

        {/* Copyright */}
        <div className="text-center">
          <p className="text-foreground/70 text-xs mb-3">
            &copy; {new Date().getFullYear()} Fuel72. {t("footer.copyright")}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3 text-xs text-foreground/60">
            <a href="/impressum" className="hover:text-neon-purple transition-colors">Impressum</a>
            <span className="text-foreground/40">•</span>
            <a href="/privacy" className="hover:text-neon-purple transition-colors">Privacy Policy</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
